import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Image as ImageIcon, Save, Trash2, Plus, RefreshCw, Loader2, Eye, Upload } from 'lucide-react';
import { toast } from 'sonner';

const POSITIONS = [
  { value: 'top-left', label: 'vlevo nahoře' },
  { value: 'top-right', label: 'vpravo nahoře' },
  { value: 'bottom-left', label: 'vlevo dole' },
  { value: 'bottom-right', label: 'vpravo dole' },
];

function emptyTemplate() {
  return {
    name: 'Overlay',
    spec: {
      max_size: 1200,
      quality: 85,
      badge: {
        enabled: false,
        text: '-{discount} %',
        position: 'top-right',
        bg_color: '#e11d48',
        text_color: '#ffffff',
        font_size_pct: 10,
      },
      price_bar: {
        enabled: false,
        text: '{price}',
        bg_color: '#0f172a',
        text_color: '#ffffff',
        font_size_pct: 7,
        height_pct: 14,
      },
      frame: { enabled: false, color: '#0f172a', width_pct: 2 },
      image: { enabled: true, asset_id: null, fit: 'stretch', opacity: 1 },
    },
  };
}

const FITS = [
  { value: 'stretch', label: 'roztáhnout přesně na fotku' },
  { value: 'contain', label: 'vejít se dovnitř (bez deformace)' },
  { value: 'original', label: 'položit 1:1 od levého horního rohu' },
];

function Section({ title, description, enabled, onToggle, children, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-slate-900">{title}</div>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} data-testid={testId} />
      </div>
      {enabled && <div className="mt-4 space-y-3">{children}</div>}
    </div>
  );
}

function ColorInput({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="h-8 w-12 rounded border border-slate-200 bg-white p-0.5"
        />
        <Input value={value} onChange={e => onChange(e.target.value)} className="h-8 text-xs w-28" />
      </div>
    </div>
  );
}

export default function ImageOverlaysPage({ embedded = false }) {
  const { projectId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [job, setJob] = useState(null);
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewSize, setPreviewSize] = useState(null);
  const [generating, setGenerating] = useState(false);
  const previewUrlRef = useRef(null);
  const initializedRef = useRef(false);

  const setSpec = (part) => setForm(f => ({ ...f, spec: { ...f.spec, ...part } }));
  const setBadge = (part) => setSpec({ badge: { ...form.spec.badge, ...part } });
  const setBar = (part) => setSpec({ price_bar: { ...form.spec.price_bar, ...part } });
  const setFrame = (part) => setSpec({ frame: { ...form.spec.frame, ...part } });
  const imageLayer = form.spec.image || { enabled: false, asset_id: null, fit: 'stretch', opacity: 1 };
  const setImageLayer = (part) => setSpec({ image: { ...imageLayer, ...part } });

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/overlay-templates`);
      setTemplates(res.data);
      if (!initializedRef.current && res.data.length > 0) {
        initializedRef.current = true;
        setSelectedId(res.data[0].id);
        setForm({ name: res.data[0].name, spec: res.data[0].spec });
      }
    } catch (err) {
      toast.error('Nepodařilo se načíst šablony');
    }
  }, [projectId]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/overlay-assets`);
      setAssets(res.data);
    } catch (err) {
      // seznam PNG není kritický
    }
  }, [projectId]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.includes('png')) {
      toast.error('Nahraj prosím PNG (kvůli průhlednosti)');
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await api.post(`/projects/${projectId}/overlay-assets`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Nahráno: ${res.data.name} (${res.data.width}×${res.data.height} px)`);
      setImageLayer({ enabled: true, asset_id: res.data.id });
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nahrání se nezdařilo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await api.delete(`/projects/${projectId}/overlay-assets/${assetId}`);
      if (imageLayer.asset_id === assetId) setImageLayer({ asset_id: null, enabled: false });
      fetchAssets();
      toast.success('PNG smazán');
    } catch (err) {
      toast.error('Smazání se nezdařilo');
    }
  };

  const fetchJob = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/image-job`);
      setJob(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  }, [projectId]);

  useEffect(() => { fetchTemplates(); fetchJob(); fetchAssets(); }, [fetchTemplates, fetchJob, fetchAssets]);

  const selectTemplate = (template) => {
    setSelectedId(template.id);
    setForm({ name: template.name, spec: template.spec });
    setPreview(null);
  };

  const newTemplate = () => {
    setSelectedId(null);
    setForm(emptyTemplate());
    setPreview(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedId) {
        await api.put(`/projects/${projectId}/overlay-templates/${selectedId}`, form);
        toast.success('Šablona uložena');
      } else {
        const res = await api.post(`/projects/${projectId}/overlay-templates`, form);
        setSelectedId(res.data.id);
        toast.success('Šablona vytvořena');
      }
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Uložení se nezdařilo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await api.delete(`/projects/${projectId}/overlay-templates/${selectedId}`);
      toast.success('Šablona smazána');
      newTemplate();
      fetchTemplates();
      fetchJob();
    } catch (err) {
      toast.error('Smazání se nezdařilo');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(
        `/projects/${projectId}/overlay-preview`,
        { spec: form.spec },
        { responseType: 'blob' }
      );
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = URL.createObjectURL(res.data);
      setPreview(previewUrlRef.current);
      setPreviewSize(res.headers['x-preview-size'] || null);
    } catch (err) {
      toast.error('Náhled se nepodařilo vytvořit');
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/projects/${projectId}/generate-images`);
      toast.success(res.data.message);
      const poll = setInterval(async () => {
        const state = await fetchJob();
        if (state && state.status !== 'running' && !state.running) {
          clearInterval(poll);
          setGenerating(false);
          if (state.status === 'done') {
            const s = state.stats || {};
            toast.success(`Hotovo — vygenerováno ${s.vygenerovano || 0}, z cache ${s.z_cache || 0}, chyb ${s.chyb || 0}`);
          } else if (state.status === 'error') {
            toast.error(`Generování selhalo: ${state.error}`);
          }
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generování se nepodařilo spustit');
      setGenerating(false);
    }
  };

  const running = generating || job?.running;

  return (
    <div className={embedded ? '' : 'p-6 lg:p-8'}>
      {!embedded && (
        <div className="flex items-center gap-4 mb-6">
          <Button asChild variant="ghost" size="icon" data-testid="back-btn">
            <Link to={`/projects/${projectId}`}>
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold text-slate-900">Overlay na fotky</h1>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Šablona</CardTitle>
            <CardDescription>
              Nahraj průhledný PNG ve stejném poměru jako fotky produktů — položí se přes ně.
              Produktům ho přiřadíš pravidlem (akce Použít overlay).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => selectTemplate(t)}
                    className={`px-3 py-1 rounded-md text-sm border ${
                      selectedId === t.id
                        ? 'bg-amber-500 text-slate-900 border-amber-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                    }`}
                    data-testid={`template-${t.id}`}
                  >
                    {t.name}
                  </button>
                ))}
                <Button variant="outline" size="sm" onClick={newTemplate} data-testid="new-template-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nová
                </Button>
              </div>
            )}

            <div className="space-y-1">
              <Label>Název šablony</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="template-name"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 text-sm cursor-pointer hover:border-amber-300">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Nahrát PNG
                  <input
                    type="file"
                    accept="image/png"
                    className="hidden"
                    onChange={e => handleUpload(e.target.files?.[0])}
                    data-testid="png-upload"
                  />
                </label>
                {previewSize && (
                  <span className="text-xs text-slate-500">
                    Fotky v náhledu mají {previewSize} px — PNG dělej ve stejném poměru.
                  </span>
                )}
              </div>

              {assets.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {assets.map(a => (
                    <div key={a.id} className="relative">
                      <button
                        onClick={() => setImageLayer({ asset_id: a.id, enabled: true })}
                        className={`block w-full rounded-md border-2 overflow-hidden bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#ffffff_0%_50%)] bg-[length:16px_16px] ${
                          imageLayer.asset_id === a.id ? 'border-amber-500' : 'border-slate-200'
                        }`}
                        title={`${a.name} — ${a.width}×${a.height} px`}
                        data-testid={`asset-${a.id}`}
                      >
                        <img src={a.url} alt={a.name} className="w-full h-20 object-contain" />
                      </button>
                      <button
                        onClick={() => handleDeleteAsset(a.id)}
                        className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-400 hover:text-red-600"
                        title="Smazat"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Jak PNG posadit</Label>
                  <select
                    value={imageLayer.fit}
                    onChange={e => setImageLayer({ fit: e.target.value })}
                    className="flex h-8 rounded-md border border-input bg-white px-2 text-sm"
                  >
                    {FITS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Průhlednost ({Math.round(imageLayer.opacity * 100)} %)</Label>
                  <input
                    type="range" min="0.1" max="1" step="0.05"
                    value={imageLayer.opacity}
                    onChange={e => setImageLayer({ opacity: parseFloat(e.target.value) })}
                    className="w-40"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Ukládám...' : selectedId ? 'Uložit šablonu' : 'Vytvořit šablonu'}
              </Button>
              <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="preview-btn">
                {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Náhled
              </Button>
              {selectedId && (
                <Button variant="outline" onClick={handleDelete} className="text-red-600" data-testid="delete-template-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Náhled + generování */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Náhled</CardTitle>
              <CardDescription>Vyrenderuje se na skutečném produktu z tohoto projektu.</CardDescription>
            </CardHeader>
            <CardContent>
              {preview ? (
                <img src={preview} alt="Náhled overlaye" className="max-w-full rounded-lg border border-slate-200" />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                  <ImageIcon className="w-10 h-10 mb-3" />
                  <p className="text-sm">Klikni na Náhled a uvidíš, jak fotka dopadne</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Generování</CardTitle>
              <CardDescription>
                Šablonu přiřadíš produktům v Pravidlech — akce „Použít overlay na fotku". Obrázky se
                pak přegenerují i při každém nočním importu.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                  Přiřazená šablona: {job?.prirazeno_sablon ?? 0} produktů
                </Badge>
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  Hotový overlay: {job?.s_overlayem ?? 0} produktů
                </Badge>
              </div>

              {job?.stats && (
                <div className="text-sm text-slate-600">
                  Poslední běh: vygenerováno {job.stats.vygenerovano}, z cache {job.stats.z_cache},
                  přeskočeno {job.stats.preskoceno}, chyb {job.stats.chyb}
                </div>
              )}
              {job?.error && <div className="text-sm text-red-600">Chyba: {job.error}</div>}

              <div className="flex gap-2">
                <Button
                  onClick={handleGenerate}
                  disabled={running}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  data-testid="generate-btn"
                >
                  {running
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generuji...</>
                    : <><RefreshCw className="w-4 h-4 mr-2" /> Vygenerovat obrázky</>}
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/projects/${projectId}/rules`}>Přiřadit pravidlem</Link>
                </Button>
              </div>

              <p className="text-xs text-slate-500">
                Produkty bez přiřazené šablony i ty, u kterých generování selže, zůstanou na původní
                fotce z e-shopu — feed se tím nikdy nerozbije.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
